/**
 * AcDbAssocArray OBJECTS-section parser — Batch-8 S-ARRAYASSOC-PARSE (L1).
 *
 * AutoCAD persists an associative array (ARRAYRECT / ARRAYPOLAR / ARRAYPATH)
 * as an ARRAY block-reference entity in the ENTITIES section PLUS a graph of
 * `AcDbAssoc*` objects in the OBJECTS section that is the REAL carrier of the
 * array parameters and the source-entity dependency:
 *
 *   ACDBASSOCNETWORK                     ← the associative network root
 *     └─ ACDBASSOCACTION                 ← the action that drives evaluation
 *          └─ ACDBASSOCARRAYACTIONBODY   ← array-specific action body
 *               ├─ ACDBASSOCARRAYMODIFYPARAMETERS / *ITEM* records (item data)
 *               └─ ACDBASSOCARRAYRECTANGULARPARAMETERS  ← decoded params
 *     └─ ACDBASSOCDEPENDENCY (one or more) ← FK to the source-entity handle(s)
 *
 * The ARRAY block-reference entity's extension dictionary (102
 * `{ACAD_XDICTIONARY` 360 …) points at the action-body / network so the
 * resolver (handleResolution.resolveAssocArrays) can join the entity to its
 * decoded parameters + source entities.
 *
 * ── Decode boundary (what is TYPED vs PRESERVED-OPAQUE) ──
 *
 * The internal group layout of the AcDbAssoc* objects is NOT part of the
 * public Autodesk DXF reference (these are ObjectARX runtime objects, exactly
 * like the dynamic-block authoring objects parsed by `parseDynamicBlockObject`).
 * We therefore follow the SAME tolerant strategy: decode only the unambiguous
 * fields and preserve every group verbatim in `rawGroups` so the writer can
 * re-emit the object byte-for-byte (no fidelity loss).
 *
 * DECODED (typed):
 *   - `handle`        — self handle (first group 5).
 *   - `ownerHandle`   — first group 330 at control-group depth 0 (mirrors
 *                       `parseDynamicBlockObject`'s 102-depth tracking so a
 *                       330 inside `{ACAD_REACTORS` is NOT mistaken for owner).
 *   - `objectType`    — the concrete subclass name (group 0).
 *   - `subclassMarkers` — the ordered list of 100-code subclass markers (so
 *                       the resolver can identify ACTIONBODY / params / dep).
 *   - For an ACDBASSOCARRAYRECTANGULARPARAMETERS record: the named numeric
 *     parameters that appear after the AcDbAssocArrayRectangularParameters
 *     subclass marker — column/row/level counts (group 90 ints) and the
 *     spacing / axis / total doubles (group 40 reals), each captured in the
 *     order AutoCAD emits them plus the optional expression strings (group 1).
 *   - For an ACDBASSOCDEPENDENCY record: the dependency's referenced object
 *     handle (group 330 at depth 0 after the AcDbAssocDependency marker) —
 *     this is the source-entity FK the resolver walks back to a model id.
 *   - For an ACDBASSOCACTION record (audit §12 S-ARRAYRECT-PARAMS-DECODE): the
 *     NAMED VALUE PARAMS at the AcDbAssocAction subclass tail. Real AutoCAD
 *     does NOT emit a standalone ACDBASSOCARRAYRECTANGULARPARAMETERS OBJECTS
 *     record — the rectangular counts / spacings live as named value params on
 *     the owning action ("Items"=column count, "Rows", "Levels",
 *     "ItemSpacing"=column spacing, "RowSpacing", "LevelSpacing",
 *     "RowElevation", "AxesAngle" [radians], "AlignItems"), and the action
 *     body inlines the parameters object as a `1 <class name>` group. Grammar
 *     verified against two independent real AutoCAD fixtures
 *     (deviantollam/TastingTurntable 2018-format + AFMD/layouts
 *     mask_layout.dxf) and the ODA reference implementation
 *     (`OdDbImpValueParam::dxfInFields`,
 *     `OdDbImpAssocArrayRectangularParameters` value-param names,
 *     `OdDbImpAssocAction::dxfInFields`).
 *   - For an ACDBASSOCARRAYACTIONBODY record: the inline parameters CLASS NAME
 *     (`1 AcDbAssocArrayRectangularParameters` / Polar / Path) right after the
 *     AcDbAssocArrayActionBody subclass marker — used to tag `paramFamily` so
 *     the resolver maps the action's named value params with the correct
 *     family semantics (polar "Items"/"ItemSpacing" mean count-around-circle /
 *     angle — NEVER mapped onto rectangular fields).
 *   - For an ACDBASSOCARRAYACTIONBODY / ACDBASSOCARRAYMODIFYACTIONBODY record
 *     (audit §12 S-ARRAYRECT-ITEMS-DECODE): the inline per-item
 *     AcDbAssocArrayItem records — grid locator, flags (erased / modified
 *     bits), the default transform (translation point OR full 4×4 matrix),
 *     the per-item relative-transform OVERRIDE (moved/rotated items — where
 *     per-item rotation lands), the entity-provider FK and the modify-body
 *     FK. Grammar: ODA `OdDbImpAssocArrayParameters::dxfInFields` +
 *     `OdDbImpAssocArrayItem::dxfInFields` (Teigha
 *     DbConstraints/DbImpAssocArrayParameters.cpp), byte-matched against the
 *     #303 real-AutoCAD fixtures (whose items are flags=8 translation-only
 *     records). Full reference on `AssocArrayItem` in types.ts. The decode is
 *     ALL-OR-NOTHING per body (a half-decoded item list would misreport the
 *     array's population) and a pure projection — the records keep
 *     round-tripping verbatim via rawGroups.
 *   - For an ACDBASSOCARRAYMODIFYACTIONBODY record: the overridden-item grid
 *     locators on the AcDbAssocArrayModifyActionBody subclass tail
 *     (ARRAYEDIT → Replace-Item; ODA
 *     `OdDbImpAssocArrayModifyActionBody::dxfOutFields` — WRITER-verified,
 *     see `overriddenItemLocators` in types.ts).
 *   - ACDBASSOCVERTEXACTIONPARAM records (named point geom params — the
 *     array's "BaseNormal" / "Origin" / "basePoint") are decoded by
 *     `collectAssocVertexActionParams` at RESOLVE time from rawObjectGroups —
 *     they are NOT diverted out of the raw stream (verbatim round-trip stays
 *     untouched; the typed view is a projection only).
 *
 * PRESERVED-OPAQUE (rawGroups only):
 *   - Everything else: the AcDbAssocAction status words, the GUID payloads,
 *     the 360/350 cross-links, the trailing 16×40 array base transform on the
 *     action body (whole-array placement — ODA composeForLoad overwrites it
 *     from the ARRAY entity's own blockTransform, so the entity grid is the
 *     authoritative carrier), POLAR/PATH-specific parameter records
 *     (forward-compat — we keep them verbatim and decode the rectangular case
 *     only). A later phase can widen the decode without losing any data
 *     because the source stream is preserved.
 *
 * ── Researched deferrals (audit §12 S-ARRAYRECT-PARAMS-DECODE) ──
 *   - xAxisDirection: AutoCAD persists a NON-DEFAULT column-axis direction as
 *     a geom param named "RowProfile" — an AcDbAssocEdgeActionParam wrapping a
 *     line whose direction is the X axis (ODA
 *     `OdDbImpAssocArrayRectangularParameters::axisDirection/setAxisDirection`;
 *     falls back to (1,0,0) when absent). No real-world fixture with a
 *     non-default axis is available to verify the edge-param curve encoding,
 *     so the decode is DEFERRED — the record round-trips verbatim via
 *     rawObjectGroups, and the model field is never default-materialized.
 *   - yAxisDirection: NEVER persisted by AutoCAD — derived at runtime as the
 *     X axis rotated by AxesAngle about the base normal (ODA
 *     `CalculateDefaultMatrix`: vY = yAxis.rotateBy(axesAngle − π/2, vZ)).
 *     Nothing to decode; deriving it would violate
 *     preserve-source-never-infer.
 *   - itemRotation: NO rectangular-array "ItemRotation" value param exists in
 *     AutoCAD's persistence (rect items get pure-translation default
 *     transforms — ODA `CalculateDefaultMatrix` returns translation-only).
 *     Per-item rotation only exists as per-item transform overrides on the
 *     inline AcDbAssocArrayItem records — DECODED by
 *     S-ARRAYRECT-ITEMS-DECODE as `AssocArrayItem.relativeTransform` (the
 *     rotation lives inside the 4×4 override matrix, never re-derived into a
 *     scalar — preserve-source-never-infer).
 *
 * ── Researched deferrals (audit §12 S-ARRAYRECT-ITEMS-DECODE) ──
 *   - item ↔ ORIGINAL-source-entity map ("Item source mapping[]"): NOT
 *     persisted in DXF. The only per-item entity link AutoCAD writes is the
 *     item's entityProviderId (its display block-reference inside the array's
 *     anonymous *U block — ODA `OdDbAssocArrayItem::entityProvider`, decoded
 *     as `entityProviderHandle`); the ORIGINAL source entities are
 *     action-level AcDbAssocDependency FKs shared by every item (decoded as
 *     `sourceEntityIds`). A per-item source map would be a derivation
 *     (provider → *U block contents → deep-clone provenance) — never
 *     invented; the model's `itemSourceMapping` home stays absent.
 *   - replaced-item REPLACEMENT entity ids: the ARRAYEDIT-replace source is a
 *     deep-cloned anonymous *U BTR owned by the modify body's own dependency
 *     graph (ODA `OdDbImpAssocArrayModifyActionBody::createInstance`) — no
 *     model-entity id is persisted; the typed truth is the item's
 *     `repBodyHandle` + the modify body's `overriddenItemLocators`.
 */
import type {
  DxfGroup,
  AssocActionValueParam,
  AssocArrayItem,
  AssocArrayItemLocator,
  AssocArrayParamFamily,
  AssocArrayRectParams,
  AssocVertexActionParam,
  DxfAssocArrayObject,
} from "./types";
import { advanceControlDepth } from "./controlBlockDepth";
import { normalizeHandle } from "./handleNormalize";
import { decodeDxfUnicode } from "./dxfUnicodeEscape";

/**
 * The STRICT object types that are UNAMBIGUOUSLY array-specific by their class
 * name alone (`ACDBASSOCARRAY*` — action-body / parameter / item records).
 * Anything in this set is always pulled out of `rawObjectGroups` into the
 * typed `assocArrays[]` list (and re-emitted from `rawGroups` by the writer,
 * keeping the OBJECTS section clean).
 *
 * `ACDBASSOCNETWORK` / `ACDBASSOCACTION` are DELIBERATELY EXCLUDED here
 * (Codex #251-C): they are GENERIC ObjectARX associative-framework classes —
 * the same network/action machinery backs associative dimensions, surfaces and
 * dynamic-block parametric constraints. Treating every network/action as an
 * array anchor diverted EVERY ACDBASSOCDEPENDENCY owned by any network/action
 * out of `rawObjectGroups`, so `collectAssocConstraintRecords` (the
 * dynamic-block constraint importer) lost those depChains → constraint params
 * lost `associatedEntities`. A network/action only belongs to the array graph
 * when it is PROVEN connected to an `ACDBASSOCARRAY*` object — see
 * `computeAssocArrayCoreHandles` (reachability proof) and the candidate
 * predicate `isAssocArrayGraphCandidateType` below.
 */
export function isAssocArrayStrictObjectType(type: string): boolean {
  const v = type.trim().toUpperCase();
  return (
    v === "ACDBASSOCARRAYACTIONBODY" ||
    v === "ACDBASSOCARRAYMODIFYACTIONBODY" ||
    v === "ACDBASSOCARRAYMODIFYPARAMETERS" ||
    v === "ACDBASSOCARRAYRECTANGULARPARAMETERS" ||
    v === "ACDBASSOCARRAYPOLARPARAMETERS" ||
    v === "ACDBASSOCARRAYPATHPARAMETERS" ||
    v === "ACDBASSOCARRAYITEM" ||
    // Forward-compat: any ACDBASSOCARRAY* subclass we have not enumerated
    // still belongs to the array graph and round-trips verbatim.
    /^ACDBASSOCARRAY.+/.test(v)
  );
}

/**
 * Whether `type` is a GENERIC associative-framework class that MAY anchor an
 * array graph (`ACDBASSOCNETWORK` / `ACDBASSOCACTION`) but is NOT
 * array-specific by name. A record of this type is diverted into the typed
 * `assocArrays[]` list ONLY when its handle is in the reachability-proven core
 * set (`computeAssocArrayCoreHandles`) — otherwise it stays in
 * `rawObjectGroups` together with its dependencies so the dynamic-block
 * constraint importer keeps seeing the full generic graph (Codex #251-C).
 */
export function isAssocArrayGraphCandidateType(type: string): boolean {
  const v = type.trim().toUpperCase();
  return v === "ACDBASSOCNETWORK" || v === "ACDBASSOCACTION";
}

/**
 * Whether `type` CAN participate in an AcDbAssocArray graph: the strict
 * `ACDBASSOCARRAY*` family plus the generic network/action candidates.
 *
 * NOTE (Codex #251-C): this is a TYPE-LEVEL membership test only — it is NOT
 * the divert decision. A generic network/action additionally requires the
 * reachability proof (`computeAssocArrayCoreHandles`) before it may be pulled
 * out of `rawObjectGroups`. Kept exported for consumers/tests that need the
 * candidate union.
 */
export function isAssocArrayCoreObjectType(type: string): boolean {
  return isAssocArrayStrictObjectType(type) || isAssocArrayGraphCandidateType(type);
}

/**
 * Whether `type` is a CANDIDATE object type for the AcDbAssocArray graph. This
 * is the union of the core array types PLUS the shared `ACDBASSOCDEPENDENCY`
 * type. It does NOT, by itself, mean the record should be diverted out of
 * `rawObjectGroups`: a dependency is only diverted when it is reachable from an
 * array graph (see the reachability-scoped dispatch). Use
 * `isAssocArrayCoreObjectType` for the always-divert decision.
 */
export function isAssocArrayObjectType(type: string): boolean {
  const v = type.trim().toUpperCase();
  return v === "ACDBASSOCDEPENDENCY" || isAssocArrayCoreObjectType(v);
}

/**
 * Single depth-aware scan of an AcDbAssoc* object body: extracts the self
 * handle (first group 5), the owner handle (first group 330 at control-block
 * depth 0 — a 330 inside a 102 control block is a reactor cross-link, NOT the
 * owner, mirroring `parseDynamicBlockObject`), the ordered 100-code subclass
 * markers, and the depth-0 POINTER REFS — every depth-0 360 hard-owner ref
 * plus every depth-0 330 AFTER the owner (an AcDbAssocAction points at its
 * action body via 360; an AcDbAssocNetwork lists its owned actions as
 * post-owner 330s inside the AcDbAssocNetwork subclass). Null refs
 * (`360 0` / `330 0`) are skipped — they are explicit empty slots, not edges.
 * The pointer refs feed the `computeAssocArrayCoreHandles` reachability proof
 * (Codex #251-C); reactor 330/360s inside `{ACAD_REACTORS` / `{ACAD_XDICTIONARY`
 * control blocks are excluded via the same depth tracking.
 *
 * This is the SINGLE source of truth for the handle/owner/marker scan, shared
 * by `decodeAssocArrayObject` (full decode), `readAssocObjectOwner` (the
 * lightweight classifier the reachability-scoped dispatch uses) and
 * `computeAssocArrayCoreHandles` (the reachability walk).
 */
function scanAssocObjectHeader(groups: readonly DxfGroup[]): {
  handle: string;
  ownerHandle: string | undefined;
  subclassMarkers: string[];
  pointerRefs: string[];
} {
  let handle = "";
  let ownerHandle: string | undefined;
  let controlDepth = 0;
  const subclassMarkers: string[] = [];
  const pointerRefs: string[] = [];
  for (const g of groups) {
    const nextDepth = advanceControlDepth(g, controlDepth);
    if (nextDepth !== undefined) {
      controlDepth = nextDepth;
      continue;
    }
    if (g.code === 5 && handle === "") {
      handle = g.value.trim();
      continue;
    }
    if (g.code === 100) {
      subclassMarkers.push(g.value.trim());
      continue;
    }
    if (g.code === 330 && controlDepth === 0) {
      if (ownerHandle === undefined) {
        ownerHandle = g.value.trim();
      } else {
        // Post-owner depth-0 330 — an edge to another assoc object (e.g. a
        // network's owned-action list). Null refs are empty slots — skip.
        const h = g.value.trim();
        if (h.length > 0 && h !== "0") pointerRefs.push(h);
      }
      continue;
    }
    if (g.code === 360 && controlDepth === 0) {
      // Depth-0 hard-owner ref (e.g. action → action body). Null refs skipped.
      const h = g.value.trim();
      if (h.length > 0 && h !== "0") pointerRefs.push(h);
    }
  }
  return { handle, ownerHandle, subclassMarkers, pointerRefs };
}

/**
 * Extract the owner handle (first group 330 at control-block depth 0) and the
 * self handle (first group 5) from a raw object group list WITHOUT a full
 * decode. Used by the reachability-scoped dispatch to classify
 * `ACDBASSOCDEPENDENCY` records: a dependency owned by an array core object
 * belongs to the array graph; any other dependency (generic / constraint) is
 * left in `rawObjectGroups` for `collectAssocConstraintRecords`.
 */
export function readAssocObjectOwner(groups: readonly DxfGroup[]): {
  handle: string;
  ownerHandle: string | undefined;
} {
  const { handle, ownerHandle } = scanAssocObjectHeader(groups);
  return { handle, ownerHandle };
}

// AssocArrayParamFamily / AssocArrayRectParams / DxfAssocArrayObject (and the
// S-ARRAYRECT-PARAMS-DECODE companions AssocActionValueParam /
// AssocVertexActionParam) are defined in ./types (alongside DxfObjectsSection,
// which references DxfAssocArrayObject) so there is no types ↔ assocArrayParser
// import cycle. Imported above for local use; re-exported here for existing
// consumers that import them from this module.
export type {
  AssocActionValueParam,
  AssocArrayItem,
  AssocArrayItemLocator,
  AssocArrayParamFamily,
  AssocArrayRectParams,
  AssocVertexActionParam,
  DxfAssocArrayObject,
};

/**
 * A pre-split OBJECTS record: the `code 0` type name plus the full group list
 * (header included). This is the shared input shape of
 * `computeAssocArrayCoreHandles` — both `collectAssocArrays` (flat-stream
 * collector) and `parseObjectsSection` (section dispatch) already split their
 * streams into exactly this shape.
 */
export type AssocObjectRecord = {
  readonly type: string;
  readonly groups: readonly DxfGroup[];
};

/**
 * Compute the NORMALIZED handle set of every object that is PROVEN to belong
 * to an AcDbAssocArray graph (Codex #251-C reachability proof):
 *
 *   1. Every STRICT `ACDBASSOCARRAY*` record (action-body / params / item) is
 *      array-core by its class name alone — its handle seeds the set.
 *   2. A generic `ACDBASSOCACTION` is array-core ONLY when connected to an
 *      array object: it OWNS one (an `ACDBASSOCARRAYACTIONBODY*`'s depth-0
 *      owner 330 names the action) or it POINTS at one (the action's depth-0
 *      360/post-owner-330 refs name an array object).
 *   3. An `ACDBASSOCNETWORK` is array-core ONLY when it owns / points at an
 *      array-core action (or, degenerately, an array object directly). The
 *      proof iterates to a fixpoint so the network is reached through its
 *      proven action (network → action → body is two ownership hops).
 *
 * Generic networks/actions that fail the proof (associative dimensions,
 * surfaces, dynamic-block constraint machinery) are NOT in the returned set,
 * so the dispatch leaves them — and every ACDBASSOCDEPENDENCY they own — in
 * `rawObjectGroups` for `collectAssocConstraintRecords`.
 *
 * All set keys are `normalizeHandle`-normalized and every edge comparison is
 * normalized too (Codex #251-A composes with the reachability walk): DXF
 * handles are case-insensitive hex, so `5 ACT1` and `330 act1` are the SAME
 * edge. Callers MUST normalize their lookups against the returned set.
 */
export function computeAssocArrayCoreHandles(
  records: readonly AssocObjectRecord[],
): ReadonlySet<string> {
  // Normalized handles of every proven array-core object (strict + proven
  // candidates). Grown by the fixpoint loop below.
  const coreHandles = new Set<string>();
  // Normalized OWNER handles named by proven array-core objects — "handles
  // that own an array-core object". A candidate whose self handle appears
  // here owns part of the array graph (proof rule (a)).
  const coreOwnerKeys = new Set<string>();

  type Candidate = {
    handleKey: string;
    ownerKey: string | undefined;
    pointerKeys: readonly string[];
    proven: boolean;
  };
  const candidates: Candidate[] = [];

  for (const rec of records) {
    if (isAssocArrayStrictObjectType(rec.type)) {
      const { handle, ownerHandle } = scanAssocObjectHeader(rec.groups);
      if (handle) coreHandles.add(normalizeHandle(handle));
      if (ownerHandle) coreOwnerKeys.add(normalizeHandle(ownerHandle));
    } else if (isAssocArrayGraphCandidateType(rec.type)) {
      const { handle, ownerHandle, pointerRefs } = scanAssocObjectHeader(
        rec.groups,
      );
      if (!handle) continue;
      candidates.push({
        handleKey: normalizeHandle(handle),
        ownerKey: ownerHandle ? normalizeHandle(ownerHandle) : undefined,
        pointerKeys: pointerRefs.map(normalizeHandle),
        proven: false,
      });
    }
  }

  // Fixpoint: each pass proves candidates connected to the current core set;
  // a proven action exposes its owner network on the next pass. Terminates in
  // ≤ candidates.length passes (each productive pass proves ≥ 1 candidate).
  let changed = true;
  while (changed) {
    changed = false;
    for (const c of candidates) {
      if (c.proven) continue;
      const ownsCoreObject = coreOwnerKeys.has(c.handleKey);
      const pointsAtCoreObject = c.pointerKeys.some((k) => coreHandles.has(k));
      if (ownsCoreObject || pointsAtCoreObject) {
        c.proven = true;
        coreHandles.add(c.handleKey);
        if (c.ownerKey) coreOwnerKeys.add(c.ownerKey);
        changed = true;
      }
    }
  }

  return coreHandles;
}

/**
 * Pre-scan a flat OBJECTS group stream and return every AcDbAssoc* record
 * that belongs to an associative-array graph. Mirrors `collectAssocVariables`:
 * splits on `code: 0` headers, keeps only the array-graph object types, and
 * hands each slice to `decodeAssocArrayObject`.
 *
 * Returns an empty array when no associative-array objects are present (the
 * common case for drawings without arrays).
 */
export function collectAssocArrays(
  groups: readonly DxfGroup[],
): DxfAssocArrayObject[] {
  // Split the flat stream into discrete `code: 0` records once.
  type Record = { type: string; groups: DxfGroup[] };
  const records: Record[] = [];
  let current: Record | null = null;
  for (const g of groups) {
    if (g.code === 0) {
      current = { type: g.value.trim(), groups: [g] };
      records.push(current);
      continue;
    }
    if (current) current.groups.push(g);
  }

  // Pass 1 — reachability-proven core handles (NORMALIZED — Codex #251-A/-C):
  // strict ACDBASSOCARRAY* objects plus only those networks/actions proven
  // connected to an array object. See `computeAssocArrayCoreHandles`.
  const coreHandles = computeAssocArrayCoreHandles(records);

  // Pass 2 — emit every STRICT array object; a generic network/action ONLY
  // when reachability-proven (otherwise it stays in the raw stream for the
  // constraint importer — Codex #251-C); and any ACDBASSOCDEPENDENCY whose
  // owner is a proven core object. Owner/handle lookups are NORMALIZED (DXF
  // handles are case-insensitive hex — `5 ACT1` vs `330 act1` is the same
  // edge, Codex #251-A); the stored payloads keep the ORIGINAL strings.
  const out: DxfAssocArrayObject[] = [];
  for (const rec of records) {
    if (isAssocArrayStrictObjectType(rec.type)) {
      out.push(decodeAssocArrayObject(rec.type, rec.groups));
      continue;
    }
    if (isAssocArrayGraphCandidateType(rec.type)) {
      const { handle } = readAssocObjectOwner(rec.groups);
      if (handle && coreHandles.has(normalizeHandle(handle))) {
        out.push(decodeAssocArrayObject(rec.type, rec.groups));
      }
      continue;
    }
    if (rec.type.trim().toUpperCase() === "ACDBASSOCDEPENDENCY") {
      const { ownerHandle } = readAssocObjectOwner(rec.groups);
      if (
        ownerHandle !== undefined &&
        coreHandles.has(normalizeHandle(ownerHandle))
      ) {
        out.push(decodeAssocArrayObject(rec.type, rec.groups));
      }
    }
  }

  return out;
}

/**
 * Decode a single AcDbAssoc* object body (the full group list including the
 * `code: 0` header). Exported for unit-test convenience — production callers
 * use `collectAssocArrays`.
 */
export function decodeAssocArrayObject(
  objectType: string,
  groups: DxfGroup[],
): DxfAssocArrayObject {
  // Single depth-aware header scan (shared with readAssocObjectOwner) — picks
  // the self handle, the owner (first depth-0 330, skipping reactor 102
  // blocks), and the ordered subclass markers.
  const typeUpper = objectType.trim().toUpperCase();
  const { handle, ownerHandle, subclassMarkers } =
    scanAssocObjectHeader(groups);

  const result: DxfAssocArrayObject = {
    handle,
    objectType,
    subclassMarkers,
    paramFamily: "none",
    rawGroups: groups.slice(),
  };
  if (ownerHandle !== undefined) result.ownerHandle = ownerHandle;

  // ── Subclass-specific decode ──
  if (typeUpper === "ACDBASSOCARRAYRECTANGULARPARAMETERS") {
    result.paramFamily = "rectangular";
    // All-or-nothing: a malformed numeric in the params block rejects the
    // typed view (raw round-trips verbatim) — see decodeRectParams doc.
    const rectParams = decodeRectParams(groups);
    if (rectParams !== undefined) result.rectParams = rectParams;
  } else if (typeUpper === "ACDBASSOCARRAYPOLARPARAMETERS") {
    result.paramFamily = "polar";
  } else if (typeUpper === "ACDBASSOCARRAYPATHPARAMETERS") {
    result.paramFamily = "path";
  } else if (
    typeUpper === "ACDBASSOCARRAYMODIFYPARAMETERS" ||
    typeUpper === "ACDBASSOCARRAYMODIFYACTIONBODY"
  ) {
    result.paramFamily = "modify";
  }

  // ── Real-AutoCAD shape (audit §12 S-ARRAYRECT-PARAMS-DECODE) ──
  // The action body inlines the parameters object; the `1 <class name>` group
  // right after the AcDbAssocArrayActionBody marker identifies the family
  // (real AutoCAD never emits a standalone *PARAMETERS record). Decode the
  // verbatim class name on both body flavours; project it onto `paramFamily`
  // only for the plain action body (the modify body keeps its "modify" tag).
  if (
    typeUpper === "ACDBASSOCARRAYACTIONBODY" ||
    typeUpper === "ACDBASSOCARRAYMODIFYACTIONBODY"
  ) {
    const className = decodeBodyParamsClassName(groups);
    if (className !== undefined) {
      result.paramsClassName = className;
      if (typeUpper === "ACDBASSOCARRAYACTIONBODY") {
        const family = paramsClassNameToFamily(className);
        if (family !== undefined) result.paramFamily = family;
      }
    }
    // ── Audit §12 S-ARRAYRECT-ITEMS-DECODE — inline per-item records ──
    // Both body flavours inline the item list through the SAME base-class
    // grammar (ODA OdDbImpAssocArrayParameters::dxfInFields — the modify
    // body's params object is AcDbAssocArrayModifyParameters, a plain
    // subclass that adds no DXF fields of its own). All-or-nothing +
    // absence-preserving: `items` is set only when the whole list decodes
    // against the verified grammar.
    const decodedItems = decodeBodyArrayItems(groups);
    if (decodedItems !== undefined) {
      result.itemsClassName = decodedItems.itemsClassName;
      result.items = decodedItems.items;
    }
  }

  // ARRAYEDIT → Replace-Item: the modify body's overridden-item locators on
  // its own subclass tail (S-ARRAYRECT-ITEMS-DECODE; ODA writer-verified
  // grammar — see `overriddenItemLocators` in types.ts).
  if (typeUpper === "ACDBASSOCARRAYMODIFYACTIONBODY") {
    const locators = decodeModifyBodyOverriddenLocators(groups);
    if (locators !== undefined) result.overriddenItemLocators = locators;
  }

  // The action record carries the named value params (counts / spacings /
  // AxesAngle for the rectangular family). Absence-preserving: only set when
  // at least one parameter decoded.
  if (typeUpper === "ACDBASSOCACTION") {
    const valueParams = decodeAssocActionValueParams(groups);
    if (valueParams.length > 0) result.valueParams = valueParams;
  }

  if (typeUpper === "ACDBASSOCDEPENDENCY") {
    result.dependencyRefHandle = decodeDependencyRef(groups);
  }

  return result;
}

/**
 * Map the inline parameters class name (the action body's `1 <class name>`
 * group) onto the typed family tag. Unknown class names return undefined so
 * the body keeps its current tag (forward-compat — the verbatim string is
 * still preserved on `paramsClassName`).
 */
function paramsClassNameToFamily(
  className: string,
): AssocArrayParamFamily | undefined {
  switch (className.trim()) {
    case "AcDbAssocArrayRectangularParameters":
      return "rectangular";
    case "AcDbAssocArrayPolarParameters":
      return "polar";
    case "AcDbAssocArrayPathParameters":
      return "path";
    case "AcDbAssocArrayModifyParameters":
      return "modify";
    default:
      return undefined;
  }
}

/**
 * Decode the inline parameters CLASS NAME on an ACDBASSOCARRAYACTIONBODY /
 * ACDBASSOCARRAYMODIFYACTIONBODY record: the FIRST group 1 after the
 * AcDbAssocArrayActionBody (or AcDbAssocArrayModifyActionBody) subclass
 * marker. Later `1 AcDbAssocArrayItem` groups open the inline per-item
 * sub-records and are NOT the class name — only the first group 1 counts.
 *
 * Real-fixture shape (deviantollam/TastingTurntable, AutoCAD 2018 format):
 *
 *   100 AcDbAssocActionBody → 90 <ver>
 *   100 AcDbAssocArrayActionBody → 90 <ver>
 *   1   AcDbAssocArrayRectangularParameters   ← decoded here
 *   90 <ver> → 90 <item count> → 1 AcDbAssocArrayItem → 100 ... (items)
 *   16×40 <array base transform matrix>
 */
function decodeBodyParamsClassName(
  groups: readonly DxfGroup[],
): string | undefined {
  let pastBodyMarker = false;
  for (const g of groups) {
    if (g.code === 100) {
      const m = g.value.trim();
      if (
        m === "AcDbAssocArrayActionBody" ||
        m === "AcDbAssocArrayModifyActionBody"
      ) {
        pastBodyMarker = true;
      }
      continue;
    }
    if (pastBodyMarker && g.code === 1) return g.value;
  }
  return undefined;
}

/**
 * AcDbAssocArrayItem flag bits — ODA `OdDbImpAssocArrayItem::Flags`
 * (Teigha DbConstraints/DbImpAssocArrayParameters.h; the misspellings are
 * ODA's own identifiers, kept verbatim for greppability against the
 * reference). `kChanged` carries no DXF payload; any bit OUTSIDE this set is
 * treated as an unverified grammar extension and aborts the all-or-nothing
 * decode (an unknown bit could gate extra groups, and mis-associating them
 * would corrupt every later item field).
 */
const ITEM_FLAG_ERASED = 1 << 0; // kErased
const ITEM_FLAG_RELATIVE_TRANSFORM = 1 << 1; // kRealtiveTransform [sic]
const ITEM_FLAG_DEFAULT_TRANSFORM = 1 << 2; // kDefaulTransform [sic]
const ITEM_FLAG_ITEM_PRESENT = 1 << 3; // kItemPresent
const ITEM_FLAG_MODIFIED = 1 << 4; // kModified
const ITEM_FLAG_CHANGED = 1 << 5; // kChanged (no DXF payload)
const ITEM_FLAGS_KNOWN_MASK =
  ITEM_FLAG_ERASED |
  ITEM_FLAG_RELATIVE_TRANSFORM |
  ITEM_FLAG_DEFAULT_TRANSFORM |
  ITEM_FLAG_ITEM_PRESENT |
  ITEM_FLAG_MODIFIED |
  ITEM_FLAG_CHANGED;

/**
 * Decode the inline per-item AcDbAssocArrayItem records on an
 * ACDBASSOCARRAYACTIONBODY / ACDBASSOCARRAYMODIFYACTIONBODY record — audit
 * §12 S-ARRAYRECT-ITEMS-DECODE.
 *
 * Grammar (ODA `OdDbImpAssocArrayActionBody::dxfInFields` →
 * `OdDbImpAssocArrayParameters::dxfInFields` →
 * `OdDbImpAssocArrayItem::dxfInFields`, byte-matched against the #303
 * real-AutoCAD fixtures — full per-field reference on `AssocArrayItem` in
 * types.ts):
 *
 *   100 AcDbAssocArrayActionBody → 90 <body ver>
 *   1   <params class name>                  ← decodeBodyParamsClassName
 *   90  <params ver, 0> → 90 <item count>
 *   1   <item class name>                    ← ONE group for the whole list
 *   count × item records (each `100 <item class>` …)
 *   16×40 <array base transform>             ← stays raw (whole-array)
 *
 * ALL-OR-NOTHING: any deviation from the verified grammar (wrong code at a
 * strictly positional slot, non-finite numeric, unknown flag bit, item
 * version ≠ 0 [ODA itself refuses those with eMakeMeProxy], or MORE item
 * markers than the declared count) returns undefined — a partial list would
 * misreport the array's population. ABSENCE-PRESERVING: a body without an
 * inline params block, or with a persisted item count of 0, returns
 * undefined (nothing is invented). Either way the body record round-trips
 * verbatim via rawGroups — this decode is a pure projection.
 */
/**
 * Strict decimal-integer read for this file's typed-decode grammars
 * (S-ARRAYRECT-ITEMS-DECODE items/locators; S-ARRAYRECT-PARAMS-DECODE
 * rect-params + value-params). `Number.parseInt` accepts PARTIAL numbers
 * ("1abc" → 1, "1.5" → 1), which would project a corrupted version /
 * locator / flags / count value instead of triggering the caller's bail.
 * The trimmed value must match the full integer grammar; any deviation →
 * undefined → the caller's existing rejection path (typed view absent,
 * raw round-trip untouched).
 */
// Exported (@codex P3, local review round-3) so `assocArrayPatch.ts`'s
// write-back "is this numValues really 1 (not a formula chain)?" guard uses
// the EXACT SAME integer grammar this decoder does — a valid DXF integer
// with a leading zero (e.g. "01") was accepted here as 1 but rejected by a
// narrower `/^\+?1$/` string-literal regex on the write side, wrongly
// classifying a genuinely single-value (patchable) param as
// expression-guarded. One function, both directions, can never drift apart
// again (mirrors the `RECT_NAMED_VALUE_PARAMS` read/write sharing pattern).
export function parseStrictInt(raw: string): number | undefined {
  const v = raw.trim();
  if (!/^[+-]?\d+$/.test(v)) return undefined;
  const n = Number.parseInt(v, 10);
  return Number.isFinite(n) ? n : undefined;
}

/**
 * Strict float read for this file's typed-decode grammars — the real-number
 * twin of `parseStrictInt`. `Number.parseFloat` accepts PARTIAL numbers
 * ("25mm" → 25, "1.0abc" → 1), which would project a truncated position /
 * spacing / matrix value instead of triggering the caller's bail. The
 * trimmed value must match the full float grammar; any deviation →
 * undefined → the caller's existing rejection path (typed view absent, raw
 * round-trip untouched).
 */
function parseStrictFloat(raw: string): number | undefined {
  const v = raw.trim();
  if (!/^[+-]?(\d+\.?\d*|\.\d+)([eE][+-]?\d+)?$/.test(v)) return undefined;
  const n = Number.parseFloat(v);
  return Number.isFinite(n) ? n : undefined;
}

export function decodeBodyArrayItems(
  groups: readonly DxfGroup[],
): { itemsClassName: string; items: AssocArrayItem[] } | undefined {
  // 1. Anchor on the BASE body marker (both flavours emit it — the modify
  //    body's own marker comes AFTER the inherited item list) and locate the
  //    inline params class name: the first code-1 group after the marker.
  //    Hitting another 100 marker first means there is no inline params
  //    block (the PR #251 synthetic shape) — absence-preserving undefined.
  let i = 0;
  while (
    i < groups.length &&
    !(
      groups[i]!.code === 100 &&
      groups[i]!.value.trim() === "AcDbAssocArrayActionBody"
    )
  ) {
    i += 1;
  }
  if (i >= groups.length) return undefined;
  i += 1;
  while (i < groups.length && groups[i]!.code !== 1) {
    if (groups[i]!.code === 100) return undefined;
    i += 1;
  }
  if (i >= groups.length) return undefined;
  i += 1; // skip the params class name group

  // 2. Strictly positional params header: 90 <ver=0> → 90 <count>. ODA
  //    rejects a non-zero params version (unverified grammar beyond) and a
  //    count of 0 ends the params object with no item-class group at all.
  const verG = groups[i];
  const countG = groups[i + 1];
  if (
    verG === undefined ||
    countG === undefined ||
    verG.code !== 90 ||
    countG.code !== 90
  ) {
    return undefined;
  }
  const paramsVer = parseStrictInt(verG.value);
  const count = parseStrictInt(countG.value);
  if (paramsVer !== 0 || count === undefined || count <= 0) {
    return undefined;
  }
  i += 2;

  // 3. The single item-class group for the whole list (`1 AcDbAssocArrayItem`
  //    — ODA reads ONE class string and instantiates `count` items of it).
  const classG = groups[i];
  if (classG === undefined || classG.code !== 1) return undefined;
  const itemsClassName = classG.value;
  const itemMarker = itemsClassName.trim();
  if (itemMarker.length === 0) return undefined;
  i += 1;

  // 4. count × item records. Each helper read is strictly positional —
  //    `undefined` aborts the whole decode.
  const readInt = (): number | undefined => {
    const g = groups[i];
    if (g === undefined || g.code !== 90) return undefined;
    const n = parseStrictInt(g.value);
    if (n === undefined) return undefined;
    i += 1;
    return n;
  };
  const readDouble = (code: number): number | undefined => {
    const g = groups[i];
    if (g === undefined || g.code !== code) return undefined;
    const n = parseStrictFloat(g.value);
    if (n === undefined) return undefined;
    i += 1;
    return n;
  };
  const readMatrix16 = (): number[] | undefined => {
    const m: number[] = [];
    for (let k = 0; k < 16; k += 1) {
      const n = readDouble(40);
      if (n === undefined) return undefined;
      m.push(n);
    }
    return m;
  };

  const items: AssocArrayItem[] = [];
  for (let idx = 0; idx < count; idx += 1) {
    const start = i;
    // 100 <item class> — ODA's atSubclassData(desc()->name()) check.
    const markerG = groups[i];
    if (
      markerG === undefined ||
      markerG.code !== 100 ||
      markerG.value.trim() !== itemMarker
    ) {
      return undefined;
    }
    i += 1;
    // 90 <item ver=0> → locator 3×90 → 90 <flags>.
    const itemVer = readInt();
    if (itemVer !== 0) return undefined;
    const itemIndex = readInt();
    const rowIndex = readInt();
    const levelIndex = readInt();
    const flags = readInt();
    if (
      itemIndex === undefined ||
      rowIndex === undefined ||
      levelIndex === undefined ||
      flags === undefined ||
      (flags & ~ITEM_FLAGS_KNOWN_MASK) !== 0
    ) {
      return undefined;
    }
    // Default transform: full 4×4 row-major matrix (kDefaulTransform) OR a
    // pure-translation point 11/21/31 (`m_matrix.setToTranslation(point)`).
    let position: { x: number; y: number; z: number } | undefined;
    let defaultTransform: number[] | undefined;
    if ((flags & ITEM_FLAG_DEFAULT_TRANSFORM) !== 0) {
      defaultTransform = readMatrix16();
      if (defaultTransform === undefined) return undefined;
    } else {
      const x = readDouble(11);
      const y = readDouble(21);
      const z = readDouble(31);
      if (x === undefined || y === undefined || z === undefined) {
        return undefined;
      }
      position = { x, y, z };
    }
    // Relative-transform override (kRealtiveTransform) — the per-item
    // moved/rotated delta (#303's itemRotation deferral lands here).
    let relativeTransform: number[] | undefined;
    if ((flags & ITEM_FLAG_RELATIVE_TRANSFORM) !== 0) {
      relativeTransform = readMatrix16();
      if (relativeTransform === undefined) return undefined;
    }
    // Erased = kErased | !kItemPresent (ODA reader rule — both writers pair
    // the bits). A live item then carries its entity-provider 330; a
    // modified item additionally carries the modify-body 330.
    const erased =
      (flags & ITEM_FLAG_ERASED) !== 0 ||
      (flags & ITEM_FLAG_ITEM_PRESENT) === 0;
    const modified = (flags & ITEM_FLAG_MODIFIED) !== 0;
    let entityProviderHandle: string | undefined;
    if (!erased) {
      const g = groups[i];
      if (g === undefined || g.code !== 330) return undefined;
      entityProviderHandle = g.value.trim();
      i += 1;
    }
    let repBodyHandle: string | undefined;
    if (modified) {
      const g = groups[i];
      if (g === undefined || g.code !== 330) return undefined;
      repBodyHandle = g.value.trim();
      i += 1;
    }
    const item: AssocArrayItem = {
      locator: { itemIndex, rowIndex, levelIndex },
      flags,
      erased,
      modified,
      rawGroups: groups.slice(start, i),
    };
    if (position !== undefined) item.position = position;
    if (defaultTransform !== undefined) item.defaultTransform = defaultTransform;
    if (relativeTransform !== undefined) {
      item.relativeTransform = relativeTransform;
    }
    if (entityProviderHandle !== undefined) {
      item.entityProviderHandle = entityProviderHandle;
    }
    if (repBodyHandle !== undefined) item.repBodyHandle = repBodyHandle;
    items.push(item);
  }

  // 5. Structural integrity: the group AFTER the last declared item must NOT
  //    be another item marker — more items than declared means the count and
  //    the stream disagree (corrupt record → raw-only).
  const next = groups[i];
  if (
    next !== undefined &&
    next.code === 100 &&
    next.value.trim() === itemMarker
  ) {
    return undefined;
  }

  return { itemsClassName, items };
}

/**
 * Decode the overridden-item locators on an ACDBASSOCARRAYMODIFYACTIONBODY
 * record (ARRAYEDIT → Replace-Item) — audit §12 S-ARRAYRECT-ITEMS-DECODE.
 *
 * Grammar (ODA `OdDbImpAssocArrayModifyActionBody::dxfOutFields` — the ODA
 * DXF READER side is an unimplemented stub, so this is WRITER-verified; the
 * DWG filers persist the identical ver + count + locator-triples structure):
 *
 *   100 AcDbAssocArrayModifyActionBody
 *   70  <version, 0>
 *   90  <count>
 *   per locator: 90 <itemIndex> → 90 <rowIndex> → 90 <levelIndex>
 *
 * ALL-OR-NOTHING + absence-preserving, mirroring `decodeBodyArrayItems`: any
 * positional deviation returns undefined and the record stays raw-only. A
 * persisted count of 0 (a modify body whose overrides were all reset) decodes
 * to an EMPTY array — the explicit `90 0` is real source data, distinct from
 * a record missing the tail entirely.
 */
export function decodeModifyBodyOverriddenLocators(
  groups: readonly DxfGroup[],
): AssocArrayItemLocator[] | undefined {
  let i = 0;
  while (
    i < groups.length &&
    !(
      groups[i]!.code === 100 &&
      groups[i]!.value.trim() === "AcDbAssocArrayModifyActionBody"
    )
  ) {
    i += 1;
  }
  if (i >= groups.length) return undefined;
  i += 1;
  // Strictly positional: 70 <ver> → 90 <count>.
  const verG = groups[i];
  const countG = groups[i + 1];
  if (
    verG === undefined ||
    countG === undefined ||
    verG.code !== 70 ||
    countG.code !== 90
  ) {
    return undefined;
  }
  const ver = parseStrictInt(verG.value);
  const count = parseStrictInt(countG.value);
  // Version 0 is the only persisted value (ODA writes a literal 0); a higher
  // version could change the tail grammar — unverified → raw-only.
  if (ver !== 0 || count === undefined || count < 0) return undefined;
  i += 2;
  const out: AssocArrayItemLocator[] = [];
  for (let k = 0; k < count; k += 1) {
    const triple: number[] = [];
    for (let c = 0; c < 3; c += 1) {
      const g = groups[i];
      if (g === undefined || g.code !== 90) return undefined;
      const n = parseStrictInt(g.value);
      if (n === undefined) return undefined;
      triple.push(n);
      i += 1;
    }
    out.push({
      itemIndex: triple[0]!,
      rowIndex: triple[1]!,
      levelIndex: triple[2]!,
    });
  }
  // Structural integrity (mirrors decodeBodyArrayItems step 5): the locator
  // triples ARE the end of the subclass tail (ODA dxfOutFields writes them
  // last), so a locator-pattern group (code 90) remaining past the declared
  // count means the count and the stream disagree — a PARTIAL
  // overriddenItemLocators list would misreport the ARRAYEDIT replacement
  // set. All-or-nothing: reject the whole typed decode; the record stays
  // raw-only. (A SHORTFALL — declared count larger than the triples present —
  // already aborts inside the loop on the missing / wrong-code group.)
  const next = groups[i];
  if (next !== undefined && next.code === 90) return undefined;
  return out;
}

/**
 * Decode the named value parameters at the tail of an ACDBASSOCACTION record.
 *
 * Layout of the AcDbAssocAction subclass (ODA
 * `OdDbImpAssocAction::dxfInFields`, byte-matched against the real fixtures):
 *
 *   100 AcDbAssocAction
 *   90 <version=2> → 90 <unk> → 330 <network> → 360 <action body>
 *   90 <action index> → 90 <max dep index>
 *   90 <numDeps> → 360×numDeps
 *   90 0 → 90 <numOwnedParams> → 360×numOwnedParams   (geom/action params)
 *   90 0 → 90 <numValueParams> → VALUEPARAM blocks
 *
 * Each VALUEPARAM block (see `AssocActionValueParam` in types.ts):
 *
 *   90 <ver=0> → 1 <name> → 90 <unitType> → 90 <numValues>
 *   per value: 40 <real> | 90 <int32>, then 330 <input-variable id>
 *   330 <controlled-object dependency id>
 *
 * The walk is anchored on the `1 <name>` group (the ONLY code-1 use inside an
 * AcDbAssocAction subclass) and then consumes the strictly positional
 * unitType / numValues / values grammar. A block whose values are not plain
 * 40/90 numerics (e.g. a string or point EvalVariant) is SKIPPED from the
 * typed view — never guessed — and survives verbatim in rawGroups. The scan
 * stops at the next 100 marker so a derived-class section (AcDbAssocNetwork)
 * is never misread.
 */
export function decodeAssocActionValueParams(
  groups: readonly DxfGroup[],
): AssocActionValueParam[] {
  const out: AssocActionValueParam[] = [];
  let pastActionMarker = false;
  let i = 0;
  while (i < groups.length) {
    const g = groups[i]!;
    if (g.code === 100) {
      const m = g.value.trim();
      if (m === "AcDbAssocAction") {
        pastActionMarker = true;
      } else if (pastActionMarker) {
        // A derived subclass section begins (e.g. AcDbAssocNetwork) — value
        // params live in the AcDbAssocAction section only.
        break;
      }
      i += 1;
      continue;
    }
    if (!pastActionMarker || g.code !== 1) {
      i += 1;
      continue;
    }
    // `1 <name>` opens a VALUEPARAM block. The grammar after the name is
    // strictly positional: 90 unitType, 90 numValues, then the values.
    const name = g.value;
    i += 1;
    const unitG = groups[i];
    const countG = groups[i + 1];
    if (
      unitG === undefined ||
      countG === undefined ||
      unitG.code !== 90 ||
      countG.code !== 90
    ) {
      // Malformed / non-VALUEPARAM use of code 1 — skip the name, keep
      // scanning (the raw stream still round-trips verbatim).
      continue;
    }
    // Strict reads (re-audit residue of #307): a lax `parseInt` would accept
    // a TRUNCATED "1abc" unitType / "3abc" count. Deviations route into the
    // block's existing per-param semantics — a bad count skips THIS param
    // (raw groups still carried verbatim), a bad unitType leaves the optional
    // field absent (same path as the pre-existing non-numeric guard below).
    const unitType = parseStrictInt(unitG.value);
    const numValues = parseStrictInt(countG.value);
    i += 2;
    // Sanity-cap the declared count so a corrupt file cannot stall the walk.
    if (numValues === undefined || numValues < 0 || numValues > 4096) {
      continue;
    }
    const values: Array<{ code: number; value: number }> = [];
    let wellFormed = true;
    for (let v = 0; v < numValues; v += 1) {
      const valG = groups[i];
      if (valG === undefined || (valG.code !== 40 && valG.code !== 90)) {
        // Non-numeric EvalVariant (string / point / ...) — typed decode is
        // not verified for those shapes; leave this param raw-only.
        wellFormed = false;
        break;
      }
      // Strict per-value read — a truncated "25mm" / "1abc" EvalVariant
      // triggers the SAME per-param skip as a non-numeric one (this param
      // stays raw-only; later params still decode).
      const n =
        valG.code === 40
          ? parseStrictFloat(valG.value)
          : parseStrictInt(valG.value);
      if (n === undefined) {
        wellFormed = false;
        break;
      }
      values.push({ code: valG.code, value: n });
      i += 1;
      // Per-value input-variable id (330) — consume when present.
      if (groups[i]?.code === 330) i += 1;
    }
    if (!wellFormed || values.length !== numValues) continue;
    // Trailing controlled-object dependency id (330) — consume when present.
    if (groups[i]?.code === 330) i += 1;
    const param: AssocActionValueParam = { name, values };
    if (unitType !== undefined) param.unitType = unitType;
    out.push(param);
  }
  return out;
}

/**
 * Pre-scan a flat raw OBJECTS group stream (`rawObjectGroups`) and decode
 * every ACDBASSOCVERTEXACTIONPARAM record — the NAMED point geom params
 * ("BaseNormal" / "Origin" / "basePoint") real AutoCAD hangs off an assoc
 * action. Used by `resolveAssocArrays` to project the array's base-plane
 * normal onto the model WITHOUT diverting the records out of the raw stream
 * (they keep round-tripping verbatim through the raw passthrough — zero
 * writer involvement).
 *
 * A record missing its name or any point ordinate is SKIPPED from the typed
 * view (never invented) — it still round-trips verbatim.
 */
export function collectAssocVertexActionParams(
  groups: readonly DxfGroup[],
): AssocVertexActionParam[] {
  const out: AssocVertexActionParam[] = [];
  // Split the flat stream into discrete `code: 0` records (same shape as
  // collectAssocArrays) and decode each vertex-param record.
  let current: DxfGroup[] | null = null;
  const flush = (): void => {
    if (!current) return;
    const decoded = decodeVertexActionParam(current);
    if (decoded) out.push(decoded);
    current = null;
  };
  for (const g of groups) {
    if (g.code === 0) {
      flush();
      if (g.value.trim().toUpperCase() === "ACDBASSOCVERTEXACTIONPARAM") {
        current = [g];
      }
      continue;
    }
    if (current) current.push(g);
  }
  flush();
  return out;
}

/**
 * Decode one ACDBASSOCVERTEXACTIONPARAM record body (grammar in the
 * `AssocVertexActionParam` doc): name = first group 1 inside the
 * AcDbAssocActionParam subclass; point = 10/20/30 inside the
 * AcDbAssocVertexActionParam subclass; handle/owner via the shared
 * depth-aware header scan (a 330 inside `{ACAD_REACTORS` is NOT the owner).
 * Returns null when the record is not fully decodable (typed view only —
 * the raw stream is untouched either way).
 */
function decodeVertexActionParam(
  groups: readonly DxfGroup[],
): AssocVertexActionParam | null {
  const { handle, ownerHandle } = scanAssocObjectHeader(groups);
  let name: string | undefined;
  let x: number | undefined;
  let y: number | undefined;
  let z: number | undefined;
  // Which subclass section the walk is in — the name lives on
  // AcDbAssocActionParam; the point on AcDbAssocVertexActionParam. Codes in
  // other sections (e.g. the 330 dependency id on
  // AcDbAssocSingleDependencyActionParam) are ignored.
  let section: "none" | "actionParam" | "vertex" | "other" = "none";
  for (const g of groups) {
    if (g.code === 100) {
      const m = g.value.trim();
      if (m === "AcDbAssocActionParam") section = "actionParam";
      else if (m === "AcDbAssocVertexActionParam") section = "vertex";
      else section = "other";
      continue;
    }
    if (section === "actionParam" && g.code === 1 && name === undefined) {
      name = g.value;
      continue;
    }
    if (section === "vertex") {
      // Strict ordinate reads (re-audit residue of #307): a lax `parseFloat`
      // would surface a TRUNCATED point ("25mm" → 25). A malformed ordinate
      // maps to NaN so the first-occurrence slot stays claimed and the
      // existing finiteness gate below rejects the WHOLE record (typed view
      // skipped, raw stream untouched — this decoder's skip semantics).
      if (g.code === 10 && x === undefined)
        x = parseStrictFloat(g.value) ?? Number.NaN;
      else if (g.code === 20 && y === undefined)
        y = parseStrictFloat(g.value) ?? Number.NaN;
      else if (g.code === 30 && z === undefined)
        z = parseStrictFloat(g.value) ?? Number.NaN;
    }
  }
  if (
    handle === "" ||
    name === undefined ||
    x === undefined ||
    y === undefined ||
    z === undefined ||
    !Number.isFinite(x) ||
    !Number.isFinite(y) ||
    !Number.isFinite(z)
  ) {
    return null;
  }
  const result: AssocVertexActionParam = { handle, name, point: { x, y, z } };
  if (ownerHandle !== undefined) result.ownerHandle = ownerHandle;
  return result;
}

/**
 * Decode the rectangular-array parameter values that appear after the
 * `AcDbAssocArrayRectangularParameters` (or `AcDbAssocArrayParameters`)
 * subclass marker.
 *
 * AutoCAD stores the named parameters as `AcDbAssocArrayItemLocator`-style
 * value pairs: a group-1 name string followed by the value in the matching
 * type group (90 for the integer counts, 40 for the spacing / axis doubles).
 * Because the exact ordering is producer-dependent and not documented, we
 * capture BOTH a positional decode (best-effort named fields) AND the raw
 * ordered int/double/expression arrays so the resolver can apply whichever
 * convention the producer used. Anything we cannot name is still preserved in
 * `rawGroups` on the parent object.
 *
 * Positional convention (matches AutoCAD's canonical emission + our fixture):
 *   group-90 ints  : [columnCount, rowCount, levelCount]
 *   group-40 reals : [columnSpacing, rowSpacing, levelSpacing, axisAngle,
 *                     rowElevation]
 *
 * ALL-OR-NOTHING numeric contract (re-audit residue of #307): the decode is
 * purely POSITIONAL, so a malformed numeric group makes every later slot
 * ambiguous — silently skipping it would let `columnCount` absorb
 * `rowCount`'s value, and a lax `parseInt`/`parseFloat` ("3abc" → 3) would
 * project a truncated count. Any group-90/40 value that fails the strict
 * grammar rejects the WHOLE typed rect-params view (undefined); the record
 * still round-trips verbatim via rawGroups — this decode is a pure
 * projection, same convention as `decodeBodyArrayItems`.
 */
function decodeRectParams(groups: DxfGroup[]): AssocArrayRectParams | undefined {
  const intParams: number[] = [];
  const doubleParams: number[] = [];
  const expressions: string[] = [];
  let pastParamsMarker = false;

  for (const g of groups) {
    if (g.code === 100) {
      const m = g.value.trim();
      if (
        m === "AcDbAssocArrayRectangularParameters" ||
        m === "AcDbAssocArrayParameters"
      ) {
        pastParamsMarker = true;
      }
      continue;
    }
    if (!pastParamsMarker) continue;
    switch (g.code) {
      case 90: {
        const n = parseStrictInt(g.value);
        if (n === undefined) return undefined;
        intParams.push(n);
        break;
      }
      case 40: {
        const n = parseStrictFloat(g.value);
        if (n === undefined) return undefined;
        doubleParams.push(n);
        break;
      }
      case 1:
        // T9 PR-3 — code-1 expression text is `\U+`-escaped on the synthetic
        // write path (rawGroups passthrough stays verbatim); decode the typed
        // consumer-facing view so it reads back as real chars.
        expressions.push(decodeDxfUnicode(g.value));
        break;
      default:
        break;
    }
  }

  const params: AssocArrayRectParams = {
    intParams,
    doubleParams,
    expressions,
  };
  // Positional best-effort named decode (see convention above).
  if (intParams[0] !== undefined) params.columnCount = intParams[0];
  if (intParams[1] !== undefined) params.rowCount = intParams[1];
  if (intParams[2] !== undefined) params.levelCount = intParams[2];
  if (doubleParams[0] !== undefined) params.columnSpacing = doubleParams[0];
  if (doubleParams[1] !== undefined) params.rowSpacing = doubleParams[1];
  if (doubleParams[2] !== undefined) params.levelSpacing = doubleParams[2];
  if (doubleParams[3] !== undefined) params.axisAngle = doubleParams[3];
  if (doubleParams[4] !== undefined) params.rowElevation = doubleParams[4];
  return params;
}

/**
 * Decode the referenced-object handle on an ACDBASSOCDEPENDENCY record. The
 * AcDbAssocDependency carries the FK to the dependent object (the array's
 * source entity, or the array reference itself) as a group 330 at control
 * depth 0 AFTER the AcDbAssocDependency subclass marker. We pick the FIRST
 * NON-NULL such 330 — that is the dependent object the resolver walks back to
 * a model id. Reactor 330s inside a `{ACAD_REACTORS` block are skipped via
 * depth tracking.
 *
 * Null refs (`330\n0`) are SKIPPED (continue scanning to the first real
 * handle): AutoCAD frequently emits an explicit `330 0` placeholder slot
 * before the real source handle, and treating "0" as the target would make the
 * resolver report an orphan and never add the source entity (Codex #251 F3).
 * This mirrors the null-ref filtering in the other dependency decoders
 * (`decodeAssocDependencyChain` / `decodeAssocGeomDependency`).
 */
function decodeDependencyRef(groups: DxfGroup[]): string | undefined {
  let controlDepth = 0;
  let pastDepMarker = false;
  let ref: string | undefined;
  for (const g of groups) {
    const nextDepth = advanceControlDepth(g, controlDepth);
    if (nextDepth !== undefined) {
      controlDepth = nextDepth;
      continue;
    }
    if (g.code === 100 && g.value.trim() === "AcDbAssocDependency") {
      pastDepMarker = true;
      continue;
    }
    if (
      g.code === 330 &&
      pastDepMarker &&
      controlDepth === 0 &&
      ref === undefined
    ) {
      const h = g.value.trim();
      // Skip the explicit null-reference placeholder; keep scanning for the
      // first real source handle.
      if (h.length === 0 || h === "0") continue;
      ref = h;
    }
  }
  return ref;
}
